import React, { useEffect, useState } from "react";
import axios from "axios";
import Card from "components/card";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const AdminProfile = () => {
  const [adminData, setAdminData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        setLoading(true);
        setErr("");

        
        const api = axios.create({ baseURL: API_BASE });
        api.interceptors.request.use((config) => {
          const token = localStorage.getItem("token");
          if (token) config.headers.Authorization = `Bearer ${token}`;
          return config;
        });

        
        const profileRes = await api.get("/admin/profile");
        const p = profileRes.data || {};

        
        let m = {};
        try {
          const metricsRes = await api.get("/admin/metrics");
          m = metricsRes.data || {};
        } catch {
          
          m = {};
        }

        if (!isMounted) return;

        
        setAdminData({
          id: p.id ?? "—",
          name: p.name ?? "—",
          email: p.email ?? "—",
          phone: p.mobile_no ?? p.phone ?? "—",
          role: p.role ?? "Admin",
          department: p.department ?? "—",
          joinDate: (p.join_date || p.created_at)
            ? new Date(p.join_date || p.created_at).toLocaleDateString()
            : "—",
        });

        
        setMetrics({
          ordersAssigned: Number(m.orders_assigned ?? 0),
          ordersCompleted: Number(m.orders_completed ?? 0),
          ordersCancelled: Number(m.orders_cancelled ?? 0),
          averageCompletionRate:
            typeof m.average_completion_rate === "number"
              ? `${m.average_completion_rate.toFixed(1)}%`
              : typeof m.average_completion_rate === "string"
                ? m.average_completion_rate
                : "0%",
          revenue:
            typeof m.revenue === "number"
              ? m.revenue.toLocaleString("en-IN", {
                  style: "currency",
                  currency: "INR",
                  maximumFractionDigits: 0,
                })
              : m.revenue != null
                ? String(m.revenue)
                : "₹0",
        });
      } catch (error) {
        console.error("Failed to load admin profile:", error);
        let message = "Failed to load profile.";
        if (error.response?.status === 401) {
          message = "Session expired. Please log in again.";
          
        } else if (error.code === "ERR_NETWORK") {
          message = "Cannot reach server. Check your network or CORS setup.";
        }
        if (isMounted) setErr(message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full space-y-6">
        <Card extra="p-6">
          <div className="h-6 w-40 bg-gray-200 dark:bg-white/10 rounded animate-pulse" />
        </Card>
        <Card extra="p-6 space-y-3">
          <div className="h-4 w-24 bg-gray-200 dark:bg-white/10 rounded animate-pulse" />
          <div className="h-5 w-64 bg-gray-200 dark:bg-white/10 rounded animate-pulse" />
        </Card>
      </div>
    );
  }

  if (err) {
    return (
      <div className="w-full">
        <Card extra="p-6">
          <p className="text-red-600 dark:text-red-400 font-semibold">{err}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded"
          >
            Retry
          </button>
        </Card>
      </div>
    );
  }

  if (!adminData) return null;

  // Merge for rendering
  const view = {
    ...adminData,
    ordersAssigned: metrics?.ordersAssigned ?? 0,
    ordersCompleted: metrics?.ordersCompleted ?? 0,
    ordersCancelled: metrics?.ordersCancelled ?? 0,
    averageCompletionRate: metrics?.averageCompletionRate ?? "0%",
    revenue: metrics?.revenue ?? "₹0",
  };

  return (
    <div className="w-full">
      {/* Admin Details Card */}
      <Card extra={"w-full mb-6 p-6"}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
          
          <div className="flex items-center gap-6">
            
            {/* <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-white/10" /> */}
            <div>
              <h2 className="text-3xl font-bold text-navy-700 dark:text-white mb-2">
                {view.name}
              </h2>
              <p className="text-lg text-brand-600 dark:text-brand-400 font-semibold mb-1">
                {view.role}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Member since {view.joinDate}
              </p>
            </div>
          </div>

          {/* Contact Details */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-500/10 rounded-xl p-4 border border-blue-200 dark:border-blue-500/30">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider mb-2">
                Email
              </p>
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 break-all">
                {view.email}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-500/10 rounded-xl p-4 border border-green-200 dark:border-green-500/30">
              <p className="text-xs text-green-600 dark:text-green-400 font-bold uppercase tracking-wider mb-2">
                Phone
              </p>
              <p className="text-sm font-semibold text-green-900 dark:text-green-200">
                {view.phone}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-500/10 rounded-xl p-4 border border-purple-200 dark:border-purple-500/30">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-bold uppercase tracking-wider mb-2">
                Department
              </p>
              <p className="text-sm font-semibold text-purple-900 dark:text-purple-200">
                {view.department}
              </p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-500/10 rounded-xl p-4 border border-orange-200 dark:border-orange-500/30">
              <p className="text-xs text-orange-600 dark:text-orange-400 font-bold uppercase tracking-wider mb-2">
                Admin ID
              </p>
              <p className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                {view.id}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Order Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card extra={"p-6"}>
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold uppercase mb-2">
              Orders Assigned
            </p>
            <p className="text-4xl font-bold text-brand-600 dark:text-brand-400">
              {view.ordersAssigned}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
              Total orders distributed
            </p>
          </div>
        </Card>

        <Card extra={"p-6"}>
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold uppercase mb-2">
              Orders Completed
            </p>
            <p className="text-4xl font-bold text-green-600 dark:text-green-400">
              {view.ordersCompleted}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
              Successfully delivered
            </p>
          </div>
        </Card>

        <Card extra={"p-6"}>
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold uppercase mb-2">
              Orders Cancelled
            </p>
            <p className="text-4xl font-bold text-red-600 dark:text-red-400">
              {view.ordersCancelled}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
              Cancelled orders
            </p>
          </div>
        </Card>

        <Card extra={"p-6"}>
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold uppercase mb-2">
              Completion Rate
            </p>
            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">
              {view.averageCompletionRate}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
              Orders completed on time
            </p>
          </div>
        </Card>
      </div>

      
      <Card extra={"w-full p-6"}>
        <h3 className="text-2xl font-bold text-navy-700 dark:text-white mb-6">
          Order Summary
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          <div>
            <h4 className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase mb-4">
              Status Overview
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Completed
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500"
                      style={{
                        width:
                          (view.averageCompletionRate || "0")
                            .toString()
                            .replace("%", "") + "%",
                      }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {view.averageCompletionRate}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Cancelled
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500" style={{ width: "4.7%" }} />
                  </div>
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                    4.7%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Pending
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-500" style={{ width: "18%" }} />
                  </div>
                  <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                    18%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Info */}
          <div>
            <h4 className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase mb-4">
              Revenue
            </h4>
            <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl p-6 text-white">
              <p className="text-sm opacity-90 mb-2">Total Revenue Generated</p>
              <p className="text-3xl font-bold">{view.revenue}</p>
              <p className="text-xs opacity-75 mt-3">From assigned orders</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div>
            <h4 className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase mb-4">
              Quick Actions
            </h4>
            <div className="space-y-3">
              <button className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-all duration-200">
                Edit Profile
              </button>
              <button className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all duration-200">
                View Orders
              </button>
              <button className="w-full px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all duration-200">
                Download Report
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminProfile;
